import {
  Component,
  Input,
  ViewChild,
  ElementRef,
  ChangeDetectorRef,
  OnInit
} from '@angular/core';
import { FormGroup } from '@angular/forms';
import type { Field } from '../../model/formBuilderInterface';
import { validationMessage } from '../../../../constants/constant';
import { CommonService } from '../../../shared/services/common.service';
import { REGEX } from 'src/app/core/validation';
import { Application, BucketFolders } from 'src/app/enums/application.enums';
import S3 from 'aws-sdk/clients/s3';
import { environment } from 'src/environments/environment';
import { topFanD, generateRandomId } from '@utils/common.utils';
import { STRIPE } from '@wallet/component/current-balance/stripe-document-modal/constant';
import { MOBILE_BRIDGE_EVENT_SEND } from '@constants/mobile-bridge.constant';
import { Messages } from '@enums/messages.enums';

@Component({
  selector: 'app-file-upload-input-v2',
  templateUrl: './file-upload-input-v2.component.html',
  styleUrls: ['./file-upload-input-v2.component.scss']
})
export class FormFileUploadInputV2Component implements OnInit {
  @Input() field: Field;
  @Input() form: FormGroup;
  @ViewChild('fileInput') fileInput: ElementRef;
  uploadedFileName: string;
  videosForUploading = [];
  showProgress = false;
  uploadXHR: S3.ManagedUpload;
  percent = 0;
  MOBILE_BRIDGE_EVENT_SEND = MOBILE_BRIDGE_EVENT_SEND;

  constructor(
    private readonly cs: CommonService,
    private readonly cdRef: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    const storedFileName = this.getStoredFileName();
    if (storedFileName) {
      this.uploadedFileName = storedFileName;
    }
  }

  private getStoredFileName(): string {
    if (!this.field?.fileNameFieldName) {
      return '';
    }
    return this.form.get(this.field.fileNameFieldName)?.value?.trim() || '';
  }

  private patchFileNameField(fileName: string): void {
    if (!this.field?.fileNameFieldName) {
      return;
    }
    this.form.get(this.field.fileNameFieldName)?.patchValue(fileName);
  }

  private resetFileNameField(): void {
    if (!this.field?.fileNameFieldName) {
      return;
    }
    this.form.get(this.field.fileNameFieldName)?.reset();
  }

  //moved the logic from upload service as injecting the service causing pm2 to restart
  uploadFiles(file, contentDisposition = false) {
    const params: S3.PutObjectRequest = {
      Bucket: environment.s3Config.bucketName,
      Key: file.folder + file.fileName,
      Body: file.file,
      ContentType: file.file.type
    };

    if (contentDisposition) {
      const originalFileName = file.fileName.replace(
        REGEX.PREFIX_POST_IMAGE,
        ''
      );
      params.ContentDisposition = `attachment; filename=${originalFileName}`;
    }
    const sData = topFanD(environment.sKey) || [];

    const awsConfig = new S3({
      accessKeyId: sData[0],
      secretAccessKey: sData[1],
      region: environment.s3Config.region,
      maxRetries: 0,
      httpOptions: {
        timeout: 1 * 60 * 60 * 1000, // 60 minutes timeout for large file uploads
        connectTimeout: 1 * 60 * 1000 // 60 seconds to establish connection
      }
    });
    const managedUpload = awsConfig.upload(params);

    managedUpload.send((err) => {
      if (err) {
        managedUpload.abort();
        return false;
      }
      return true;
    });
    return managedUpload;
  }

  fileChangeEventOnAddVideoButton(event: Event) {
    const input = event.target as HTMLInputElement;

    if (input.files && input.files[0]) {
      const selectedFile = input.files[0];

      if (!selectedFile.type) {
        this.cs.showErrorMessage(validationMessage.ONLY_FILES_ALLOWED);
        return;
      }

      if (
        this.field.fileSizeLimit &&
        selectedFile?.size > this.field.fileSizeLimit
      ) {
        this.cs.showErrorMessage(
          validationMessage.FILE_SIZE_LIMIT.replace(
            '[fileSize]',
            `${this.field.fileSizeLimit / (1024 * 1024)}`
          )
        );
        return;
      }
      if( this.field?.sampleFileUrl?.includes(STRIPE)){
        const control = this.form.get(this.field.name);
        control?.patchValue(selectedFile);
        return
      }
      this.uploadedFileName = selectedFile.name;
      this.patchFileNameField(selectedFile.name);
      this.videosForUploading = [];
      const fileUrl = `post_${Date.now()}_${generateRandomId()}_${generateRandomId()}`;
      const control = this.form.get(this.field.name);
      control.patchValue(
        environment.s3Config.bucketUrl +
          BucketFolders.createPostVideoFolder +
          fileUrl
      );
      const obj = {
        file: selectedFile,
        fileName: fileUrl,
        folder: BucketFolders.createPostVideoFolder
      };
      this.videosForUploading.push(obj);
      if (this.videosForUploading.length) {
        this.uploadVideoFileOnS3Bucket();
      }
    }
  }

  readonly uploadVideoFileOnS3Bucket = () => {
    this.videosForUploading.forEach((el) => {
      this.showProgress = true;
      this.cs.shareDataByEvent(this.showProgress);

      this.uploadXHR?.abort();

      const uploadXHR = this.uploadFiles(el, true);

      uploadXHR.on('httpUploadProgress', (progress) => {
        this.percent = Math.round((progress.loaded / progress.total) * 100);
        this.cdRef.detectChanges();
      });
      this.uploadXHR = uploadXHR;
      this.uploadXHR.promise().then(
        () => {
          this.uploadXHR = undefined;
          this.showProgress = false;
          this.percent = Application.ZERO;
          this.cdRef.detectChanges();
          this.cs.shareDataByEvent(this.showProgress);
        },
        () => {
          this.uploadXHR = undefined;
          this.showProgress = false;
          this.percent = Application.ZERO;
          this.cs.shareDataByEvent(this.showProgress);
          this.cs.showErrorMessage(Messages.SOMETHING_WENT_WRONG);
          this.removeVideoFromVideos();
          this.cdRef.detectChanges();
        }
      );
    });
  };

  removeVideoFromVideos() {
    const control = this.form.get(this.field.name);
    control.reset();
    this.uploadedFileName = undefined;
    this.resetFileNameField();
    this.videosForUploading = [];
    this.percent = 0;
    this.showProgress = false;
    if (this.uploadXHR) {
      this.uploadXHR.abort();
      this.uploadXHR = undefined;
    }
  }

  get displayFileName(): string {
    return this.uploadedFileName || this.getStoredFileName() || this.fileFieldName;
  }

  get fileFieldName(): string {
    const value = this.form.get(this.field.name)?.value;
    if (value?.name) {
      return value.name;
    }
    if (typeof value === 'string') {
      return validationMessage.UPLOADED_FILE;
    }
    const match = value?.match?.(REGEX.PREFIX_IMAGE_URL);
    return match?.[1] ?? validationMessage.UPLOADED_FILE;
  }
}
